import React, { useState, useEffect } from 'react';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { GripVertical, X, Check } from 'lucide-react';
import { AVAILABLE_COLUMNS, COLUMN_CATEGORIES, MAX_COLUMNS } from 'features/activity/config/userColumns';
import { usePreferences } from 'shared/context/preferencesContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { primary } from 'shared/theme/trodoColors';

const UserColumnConfigModal = ({ isOpen, onClose, teamId }) => {
  const { alert: appAlert } = useAppDialog();
  const { updateUsersTableColumns, resetUsersTableColumns, getUsersTableColumns, loading } = usePreferences();
  const [enabledColumns, setEnabledColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const current = getUsersTableColumns();
      const validEnabled = (current.enabled || []).filter(k => AVAILABLE_COLUMNS[k]);
      const validOrder = (current.order || []).filter(k => AVAILABLE_COLUMNS[k]);
      setEnabledColumns([...validEnabled]);
      setColumnOrder([...validOrder]);
      setHasChanges(false);
    }
  }, [isOpen, getUsersTableColumns]);

  const handleToggleColumn = (columnKey) => {
    const column = AVAILABLE_COLUMNS[columnKey];
    if (!column || column.required) return;
    const newEnabled = enabledColumns.includes(columnKey)
      ? enabledColumns.filter(k => k !== columnKey)
      : [...enabledColumns, columnKey];
    const requiredColumns = ['distinct_id'];
    if (newEnabled.length < 2 || !requiredColumns.every(c => newEnabled.includes(c))) return;
    if (newEnabled.length > MAX_COLUMNS && !enabledColumns.includes(columnKey)) return;
    setEnabledColumns(newEnabled);
    const newOrder = columnOrder.filter(k => newEnabled.includes(k));
    if (!enabledColumns.includes(columnKey)) newOrder.push(columnKey);
    setColumnOrder(newOrder);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await updateUsersTableColumns(teamId, enabledColumns, columnOrder);
      setHasChanges(false);
      onClose();
    } catch {
      await appAlert('Failed to save column preferences. Please try again.', { title: 'Save failed', variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await resetUsersTableColumns(teamId);
      const current = getUsersTableColumns();
      setEnabledColumns([...current.enabled]);
      setColumnOrder([...current.order]);
      setHasChanges(false);
    } catch {
      await appAlert('Failed to reset column preferences. Please try again.', { title: 'Reset failed', variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    const current = getUsersTableColumns();
    setEnabledColumns([...current.enabled]);
    setColumnOrder([...current.order]);
    setHasChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  const orderedEnabled = columnOrder.filter(k => enabledColumns.includes(k));
  const columnsByCategory = {};
  Object.keys(AVAILABLE_COLUMNS).forEach(key => {
    const col = AVAILABLE_COLUMNS[key];
    const cat = col.category || 'other';
    if (!columnsByCategory[cat]) columnsByCategory[cat] = [];
    columnsByCategory[cat].push({ key, ...col });
  });

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[color:var(--surface-overlay)]" onClick={handleCancel} />
        <div className="relative bg-surface w-full max-w-[380px] rounded-xl border border-[color:var(--border-default)] shadow-xl overflow-hidden flex flex-col" style={{ maxHeight: 'min(90vh, 560px)' }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[color:var(--border-default)] shrink-0">
            <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">Edit Columns</span>
            <div className="flex items-center gap-3">
              <button onClick={handleReset} disabled={saving || loading} className="text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors disabled:opacity-40">Reset</button>
              <button onClick={handleCancel} className="p-1 rounded hover:bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)] transition-colors"><X size={14} /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-subtle">
            {orderedEnabled.length > 0 && (
              <div className="px-3 pt-3 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)] px-1 mb-1">Active · drag to reorder</p>
                {orderedEnabled.map((key, index) => (
                  <DraggableItem
                    key={key} columnKey={key} index={index}
                    label={AVAILABLE_COLUMNS[key]?.label || key}
                    isRequired={AVAILABLE_COLUMNS[key]?.required || false}
                    onMove={(from, to) => {
                      const newOrder = [...columnOrder];
                      const [removed] = newOrder.splice(from, 1);
                      newOrder.splice(to, 0, removed);
                      setColumnOrder(newOrder);
                      setHasChanges(true);
                    }}
                  />
                ))}
              </div>
            )}
            {orderedEnabled.length > 0 && <div className="border-t border-[color:var(--border-default)] mx-3 mt-2" />}
            <div className="px-3 pt-2.5 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)] px-1 mb-1">
                Available {MAX_COLUMNS ? `(max ${MAX_COLUMNS})` : ''}
              </p>
              {Object.keys(COLUMN_CATEGORIES).map(cat => {
                const cols = columnsByCategory[cat] || [];
                if (!cols.length) return null;
                return (
                  <div key={cat} className="mb-2">
                    <p className="text-[10px] text-[color:var(--text-muted)] px-1 mb-0.5 italic">{COLUMN_CATEGORIES[cat]}</p>
                    {cols.map(({ key, label, required }) => {
                      const isEnabled = enabledColumns.includes(key);
                      const isDisabled = required || (isEnabled && enabledColumns.length <= 2) || (!isEnabled && enabledColumns.length >= MAX_COLUMNS);
                      return (
                        <button
                          key={key} type="button"
                          onClick={() => handleToggleColumn(key)}
                          disabled={isDisabled && !required}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${isDisabled && !required ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[color:var(--surface-sunken)] cursor-pointer'}`}
                        >
                          <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${isEnabled ? 'bg-[color:var(--trodo-primary)] border-[color:var(--trodo-primary)]' : 'border-[color:var(--border-strong)]'}`}>
                            {isEnabled && <Check size={9} strokeWidth={3} className="text-ink-onBrand" />}
                          </span>
                          <span className="flex-1 text-[12px] text-[color:var(--text-primary)] truncate">{label}</span>
                          {required && <span className="text-[10px] text-[color:var(--text-muted)]">required</span>}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center px-4 py-2.5 border-t border-[color:var(--border-default)] shrink-0 bg-surface">
            {hasChanges && <span className="text-[11px] text-[color:var(--text-muted)] mr-auto">Unsaved changes</span>}
            <button
              onClick={handleSave} disabled={!hasChanges || saving || loading}
              className="ml-auto px-3 py-1.5 text-[12px] font-medium text-ink-onBrand rounded-md transition-colors disabled:opacity-50"
              style={{ backgroundColor: hasChanges && !saving && !loading ? primary : 'var(--text-disabled)' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

const DraggableItem = ({ columnKey, index, label, isRequired, onMove }) => {
  const [{ isDragging }, drag] = useDrag({ type: 'column', item: () => ({ columnKey, index }), collect: m => ({ isDragging: m.isDragging() }) });
  const [{ isOver }, drop] = useDrop({
    accept: 'column',
    hover: (item) => { if (item.index !== index) { onMove(item.index, index); item.index = index; } },
    collect: m => ({ isOver: m.isOver() })
  });
  return (
    <div ref={n => drag(drop(n))} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-move transition-colors ${isDragging ? 'opacity-40' : isOver ? 'bg-[color:var(--border-subtle)]' : 'hover:bg-[color:var(--surface-sunken)]'}`}>
      <GripVertical size={13} className="text-[color:var(--border-strong)] shrink-0" />
      <span className="flex-1 text-[12px] text-[color:var(--text-primary)] truncate">{label}</span>
      {isRequired && <span className="text-[10px] text-[color:var(--text-muted)]">required</span>}
    </div>
  );
};

export default UserColumnConfigModal;
